import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  FileText,
  FileStack,
  Store,
  Settings,
  LogOut,
  ChevronRight,
  HeadphonesIcon,
  Mail,
  ExternalLink,
} from "lucide-react"
import { toast } from "sonner"

import { useAuthStore } from "@/stores/authStore"
import { APP_NAME, EMAIL_SUPPORT, MARKETPLACE_ENABLED } from "@/lib/appConfig"
import { PageHeader } from "@/components/shared/PageHeader"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

interface NavItem {
  icon: React.ReactNode
  label: string
  path: string
  description?: string
}

const NAV_ITEMS: NavItem[] = [
  {
    icon: <FileText className="h-4 w-4 text-blue-600" />,
    label: "Leases",
    path: "/leases",
    description: "Manage rental agreements",
  },
  {
    icon: <FileStack className="h-4 w-4 text-purple-600" />,
    label: "Documents",
    path: "/documents",
    description: "Store and manage files",
  },
  ...(MARKETPLACE_ENABLED ? [{
    icon: <Store className="h-4 w-4 text-green-600" />,
    label: "Marketplace",
    path: "/marketplace",
    description: "List your properties",
  }] : []),
  {
    icon: <Settings className="h-4 w-4 text-gray-600" />,
    label: "Settings",
    path: "/settings",
    description: "Account & preferences",
  },
]

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

export default function MorePage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  useEffect(() => {
    const prev = document.title
    document.title = `More | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  function handleLogout() {
    logout()
    toast.success("Signed out")
    navigate("/login")
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="More" />

      <div className="flex-1 overflow-y-auto pb-nav">
        {/* User info card */}
        <div className="px-4 pt-4 pb-3">
          <Card className="rounded-2xl border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-base font-bold text-primary">
                    {user?.full_name ? getInitials(user.full_name) : "?"}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {user?.full_name ?? "Unknown user"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email ?? ""}
                  </p>
                  {user?.plan?.name && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium capitalize">
                      {user.plan.name} plan
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation */}
        <div className="px-4 pb-4">
          <Card className="rounded-2xl border-border divide-y divide-border overflow-hidden">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{item.label}</p>
                  {item.description && (
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </Card>
        </div>

        {/* Support */}
        <div className="px-4 pb-4">
          <Card className="rounded-2xl border-border overflow-hidden">
            <CardContent className="p-0">
              <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <HeadphonesIcon className="h-3.5 w-3.5 text-primary" />
                </div>
                <p className="text-sm font-semibold">Help & Support</p>
              </div>
              <div className="px-4 py-3.5 space-y-3">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Having trouble or need help? Reach out to our support team and we'll get back to you as soon as possible.
                </p>
                <a
                  href={`mailto:${EMAIL_SUPPORT}`}
                  className="flex items-center justify-between gap-2 rounded-xl border border-border px-3.5 py-2.5 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Mail className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground truncate">{EMAIL_SUPPORT}</span>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logout */}
        <div className="px-4 pb-4">
          <Button
            variant="outline"
            className="w-full h-11 rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground/60 pb-8">
          {APP_NAME} &middot; A product of{" "}
          <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="hover:underline">
            SoulSpark Games
          </a>
        </p>
      </div>
    </div>
  )
}
